import React, { useState, useEffect } from 'react';
import '../index.css';
import { api } from '../services/authService';
import { formatCurrency } from '../utils/formatters';

export default function Cobrancas() {
  const [cobrancas, setCobrancas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalNova, setModalNova] = useState(false);
  const [modalQRCode, setModalQRCode] = useState(false);
  const [qrcodeData, setQrcodeData] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    received: 0,
    pending: 0,
    overdue: 0
  });

  const [filtros, setFiltros] = useState({
    status: '',
    type: '',
    startDate: '',
    endDate: '',
    search: ''
  });

  const [novaCobranca, setNovaCobranca] = useState({
    customer_id: '',
    value: '',
    due_date: '',
    billing_type: 'BOLETO',
    description: ''
  });

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    await Promise.all([
      carregarClientes(),
      carregarCobrancas()
    ]);
  };

  const carregarClientes = async () => {
    try {
      const response = await api.get('/api/clients');
      const data = response.data;
      setClientes(data.data || []);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
    }
  };

  const carregarCobrancas = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/charges');
      const data = response.data;
      const payments = data.data || [];
      setCobrancas(payments);
      calcularEstatisticas(payments);
    } catch (error) {
      console.error('Erro ao carregar cobranças:', error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
    }
    setLoading(false);
  };

  const calcularEstatisticas = (payments) => {
    const total = payments.length;
    let valorRecebido = 0;
    let valorPendente = 0;
    let valorVencido = 0;

    payments.forEach(p => {
      const valor = parseFloat(p.value);
      if (p.status === 'RECEIVED') valorRecebido += valor;
      else if (p.status === 'PENDING') valorPendente += valor;
      else if (p.status === 'OVERDUE') valorVencido += valor;
    });

    setStats({
      total,
      received: valorRecebido.toFixed(2),
      pending: valorPendente.toFixed(2),
      overdue: valorVencido.toFixed(2)
    });
  };

  const criarCobranca = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/charges', {
        ...novaCobranca,
        value: parseFloat(novaCobranca.value)
      });

      alert('Cobrança criada com sucesso!');
      setModalNova(false);
      setNovaCobranca({
        customer_id: '',
        value: '',
        due_date: '',
        billing_type: 'BOLETO',
        description: ''
      });
      carregarCobrancas();
    } catch (error) {
      console.error('Erro:', error);
      const message = error.response?.data?.error || 'Erro ao criar cobrança';
      alert(message);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
    }
  };

  const verQRCode = async (cobranca) => {
    if (cobranca.billing_type !== 'PIX') {
      alert('QR Code disponível apenas para pagamentos PIX');
      return;
    }

    // Simulação de QR Code - em produção viria da API do Asaas
    const qrCodeSimulado = {
      encodedImage: `data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==`,
      payload: `00020126360014BR.GOV.BCB.PIX0114+5567999991111520400005303986540${cobranca.value}5802BR5925${cobranca.client_name || 'Cliente'}6009SAO PAULO62140510PAGAMENTO${cobranca.charge_id}6304ABCD`
    };

    setQrcodeData(qrCodeSimulado);
    setModalQRCode(true);
  };

  const copiarPixCode = () => {
    navigator.clipboard.writeText(qrcodeData.payload);
    alert('Código PIX copiado para a área de transferência!');
  };

  const baixarBoleto = (cobranca) => {
    if (cobranca.billing_type !== 'BOLETO') {
      alert('Download disponível apenas para boletos');
      return;
    }

    // Construir URL usando a mesma base URL do axios
    const baseURL = api.defaults.baseURL;
    const linkBoleto = `${baseURL}/boletos/${cobranca.charge_id}.pdf`;
    window.open(linkBoleto, '_blank');
  };

  const marcarComoPago = async (cobrancaId) => {
    if (!confirm('Confirma o pagamento desta cobrança?')) return;

    try {
      await api.put(`/api/charges/${cobrancaId}/status`, {
        status: 'RECEIVED',
        received_at: new Date().toISOString()
      });

      alert('Pagamento confirmado!');
      carregarCobrancas();
    } catch (error) {
      console.error('Erro:', error);
      const message = error.response?.data?.error || 'Erro ao confirmar pagamento';
      alert(message);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
    }
  };

  const cancelarCobranca = async (cobrancaId, status) => {
    if (status === 'RECEIVED') {
      alert('Não é possível cancelar uma cobrança já recebida');
      return;
    }

    if (!confirm('Confirma o cancelamento desta cobrança? Esta ação não pode ser desfeita.')) return;

    try {
      await api.delete(`/api/charges/${cobrancaId}`);

      alert('Cobrança cancelada com sucesso!');
      carregarCobrancas();
    } catch (error) {
      console.error('Erro:', error);
      const message = error.response?.data?.error || error.response?.data?.message || 'Erro ao cancelar cobrança';
      alert(message);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
    }
  };

  const getStatusClass = (status) => {
    const classes = {
      'PENDING': 'status-pending',
      'CONFIRMED': 'status-confirmed',
      'RECEIVED': 'status-received',
      'OVERDUE': 'status-overdue',
      'CANCELLED': 'status-cancelled'
    };
    return classes[status] || 'status-pending';
  };

  const getStatusText = (status) => {
    const texts = {
      'PENDING': 'Pendente',
      'CONFIRMED': 'Confirmado',
      'RECEIVED': 'Recebido',
      'OVERDUE': 'Vencido',
      'CANCELLED': 'Cancelada'
    };
    return texts[status] || status;
  };

  const formatarData = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const cobrancasFiltradas = cobrancas.filter(c => {
    if (filtros.status && c.status !== filtros.status) return false;
    if (filtros.type && c.billing_type !== filtros.type) return false;
    if (filtros.startDate && new Date(c.due_date) < new Date(filtros.startDate)) return false;
    if (filtros.endDate && new Date(c.due_date) > new Date(filtros.endDate)) return false;
    if (filtros.search) {
      const searchLower = filtros.search.toLowerCase();
      const matchesClient = c.client_name?.toLowerCase().includes(searchLower);
      const matchesDescription = c.description?.toLowerCase().includes(searchLower);
      const matchesId = c.charge_id?.toLowerCase().includes(searchLower);
      if (!matchesClient && !matchesDescription && !matchesId) return false;
    }
    return true;
  });

  const limparFiltros = () => {
    setFiltros({
      status: '',
      type: '',
      startDate: '',
      endDate: '',
      search: ''
    });
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1>Cobranças</h1>
        <button style={styles.btnPrimary} onClick={() => setModalNova(true)}>
          + Nova Cobrança
        </button>
      </div>

      {/* Estatísticas */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <h3>Total Cobranças</h3>
          <p style={styles.statValue}>{stats.total}</p>
        </div>
        <div style={{...styles.statCard, ...styles.statSuccess}}>
          <h3>Recebidas</h3>
          <p style={styles.statValue}>R$ {stats.received}</p>
        </div>
        <div style={{...styles.statCard, ...styles.statWarning}}>
          <h3>Pendentes</h3>
          <p style={styles.statValue}>R$ {stats.pending}</p>
        </div>
        <div style={{...styles.statCard, ...styles.statDanger}}>
          <h3>Vencidas</h3>
          <p style={styles.statValue}>R$ {stats.overdue}</p>
        </div>
      </div>

      {/* Filtros */}
      <div style={styles.filters}>
        <input
          type="text"
          placeholder="Buscar por cliente, descrição ou ID..."
          value={filtros.search}
          onChange={(e) => setFiltros({...filtros, search: e.target.value})}
          style={{...styles.input, gridColumn: 'span 2'}}
        />

        <select 
          value={filtros.status} 
          onChange={(e) => setFiltros({...filtros, status: e.target.value})}
          style={styles.input}
        >
          <option value="">Todos os Status</option>
          <option value="PENDING">Pendente</option>
          <option value="CONFIRMED">Confirmado</option>
          <option value="RECEIVED">Recebido</option>
          <option value="OVERDUE">Vencido</option>
          <option value="CANCELLED">Cancelada</option>
        </select>

        <select 
          value={filtros.type} 
          onChange={(e) => setFiltros({...filtros, type: e.target.value})}
          style={styles.input}
        >
          <option value="">Todos os Tipos</option>
          <option value="BOLETO">Boleto</option>
          <option value="PIX">PIX</option>
        </select>

        <input 
          type="date" 
          placeholder="Data inicial"
          value={filtros.startDate}
          onChange={(e) => setFiltros({...filtros, startDate: e.target.value})}
          style={styles.input}
        />

        <input 
          type="date" 
          placeholder="Data final"
          value={filtros.endDate}
          onChange={(e) => setFiltros({...filtros, endDate: e.target.value})}
          style={styles.input}
        />

        <button 
          onClick={limparFiltros}
          style={{...styles.btnSecondary, ...styles.input}}
        >
          Limpar Filtros
        </button>
      </div>

      {/* Tabela */}
      {loading ? (
        <div style={styles.loading}>Carregando cobranças...</div>
      ) : cobrancasFiltradas.length === 0 ? (
        <div style={styles.emptyState}>
          <h3>Nenhuma cobrança encontrada</h3>
          <p>{cobrancas.length === 0 ? 'Crie sua primeira cobrança clicando no botão acima' : 'Tente ajustar os filtros de busca'}</p>
        </div>
      ) : (
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Cliente</th>
                <th style={styles.th}>Valor</th>
                <th style={styles.th}>Vencimento</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Tipo</th>
                <th style={styles.th}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {cobrancasFiltradas.map(c => (
                <tr key={c.id} style={styles.tr}>
                  <td style={styles.td}>
                    <div>
                      <div style={{fontWeight: '600'}}>{c.client_name || 'N/A'}</div>
                      <div style={{fontSize: '12px', color: '#666'}}>{c.description}</div>
                    </div>
                  </td>
                  <td style={styles.td}>
                    <span style={{fontWeight: '600', fontSize: '16px'}}>
                      {formatCurrency(parseFloat(c.value || 0))}
                    </span>
                  </td>
                  <td style={styles.td}>{formatarData(c.due_date)}</td>
                  <td style={styles.td}>
                    <span className={getStatusClass(c.status)} style={styles.statusBadge}>
                      {getStatusText(c.status)}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: '600',
                      backgroundColor: c.billing_type === 'PIX' ? '#e8f5e8' : '#f0f8ff',
                      color: c.billing_type === 'PIX' ? '#2d8f2d' : '#1e6ba8'
                    }}>
                      {c.billing_type}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <div style={styles.actions}>
                      {c.billing_type === 'PIX' && (
                        <button 
                          style={{...styles.btnSm, ...styles.btnQrcode}} 
                          onClick={() => verQRCode(c)}
                          title="Ver QR Code PIX"
                        >
                          QR Code
                        </button>
                      )}
                      
                      {c.billing_type === 'BOLETO' && (
                        <button 
                          style={{...styles.btnSm, ...styles.btnCopy}} 
                          onClick={() => baixarBoleto(c)}
                          title="Baixar Boleto"
                        >
                          Boleto
                        </button>
                      )}

                      {c.status === 'PENDING' && (
                        <>
                          <button 
                            style={{...styles.btnSm, ...styles.btnSuccess}} 
                            onClick={() => marcarComoPago(c.id)}
                            title="Marcar como Pago"
                          >
                            Pagar
                          </button>
                          <button 
                            style={{...styles.btnSm, ...styles.btnDanger}} 
                            onClick={() => cancelarCobranca(c.id, c.status)}
                            title="Cancelar Cobrança"
                          >
                            Cancelar
                          </button>
                        </>
                      )}

                      {c.status === 'CANCELLED' && (
                        <span style={{fontSize: '12px', color: '#dc3545', fontWeight: '600'}}>
                          CANCELADA
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal Nova Cobrança */}
      {modalNova && (
        <div style={styles.modal} onClick={() => setModalNova(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2>Nova Cobrança</h2>
              <button onClick={() => setModalNova(false)} style={styles.closeBtn}>&times;</button>
            </div>
            <form onSubmit={criarCobranca}>
              <select 
                value={novaCobranca.customer_id}
                onChange={(e) => setNovaCobranca({...novaCobranca, customer_id: e.target.value})}
                required
                style={{...styles.input, width: '100%', marginBottom: '15px'}}
              >
                <option value="">Selecione um cliente</option>
                {clientes.map(cliente => (
                  <option key={cliente.id} value={cliente.id}>
                    {cliente.name} - {cliente.document || cliente.email}
                  </option>
                ))}
              </select>

              <input 
                type="number"
                step="0.01"
                placeholder="Valor (R$)"
                value={novaCobranca.value}
                onChange={(e) => setNovaCobranca({...novaCobranca, value: e.target.value})}
                required
                style={{...styles.input, width: '100%', marginBottom: '15px'}}
              />

              <input 
                type="date"
                value={novaCobranca.due_date}
                onChange={(e) => setNovaCobranca({...novaCobranca, due_date: e.target.value})}
                required
                style={{...styles.input, width: '100%', marginBottom: '15px'}}
              />

              <select 
                value={novaCobranca.billing_type}
                onChange={(e) => setNovaCobranca({...novaCobranca, billing_type: e.target.value})}
                style={{...styles.input, width: '100%', marginBottom: '15px'}}
              >
                <option value="BOLETO">Boleto Bancário</option>
                <option value="PIX">PIX</option>
              </select>

              <input 
                type="text"
                placeholder="Descrição"
                value={novaCobranca.description}
                onChange={(e) => setNovaCobranca({...novaCobranca, description: e.target.value})}
                style={{...styles.input, width: '100%', marginBottom: '15px'}}
              />

              <div style={{display: 'flex', gap: '10px'}}>
                <button 
                  type="button" 
                  onClick={() => setModalNova(false)}
                  style={{...styles.btnSecondary, flex: 1}}
                >
                  Cancelar
                </button>
                <button type="submit" style={{...styles.btnPrimary, flex: 1}}>
                  Criar Cobrança
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal QR Code */}
      {modalQRCode && qrcodeData && (
        <div style={styles.modal} onClick={() => setModalQRCode(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2>QR Code PIX</h2>
              <button onClick={() => setModalQRCode(false)} style={styles.closeBtn}>&times;</button>
            </div>
            <div style={{textAlign: 'center', padding: '20px'}}>
              <div style={{
                width: '200px',
                height: '200px',
                backgroundColor: '#f5f5f5',
                border: '2px dashed #ccc',
                margin: '0 auto 20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '8px'
              }}>
                <div style={{textAlign: 'center', color: '#666'}}>
                  <div style={{fontSize: '48px', marginBottom: '10px'}}>📱</div>
                  <div>QR Code PIX</div>
                </div>
              </div>
              
              <div style={styles.pixCode}>
                <strong>Código PIX Copia e Cola:</strong>
                <p style={{
                  wordBreak: 'break-all', 
                  fontSize: '12px', 
                  backgroundColor: '#f8f9fa',
                  padding: '10px',
                  borderRadius: '4px',
                  margin: '10px 0'
                }}>
                  {qrcodeData.payload}
                </p>
              </div>
              
              <div style={{display: 'flex', gap: '10px', justifyContent: 'center'}}>
                <button 
                  onClick={copiarPixCode} 
                  style={{...styles.btnPrimary}}
                >
                  Copiar Código PIX
                </button>
                <button 
                  onClick={() => setModalQRCode(false)} 
                  style={{...styles.btnSecondary}}
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '20px'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '30px'
  },
  btnPrimary: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    padding: '12px 24px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600'
  },
  btnSecondary: {
    background: '#f8f9fa',
    color: '#495057',
    border: '1px solid #dee2e6',
    padding: '12px 24px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600'
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px',
    marginBottom: '30px'
  },
  statCard: {
    background: 'white',
    padding: '24px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
  },
  statSuccess: { color: '#27ae60' },
  statWarning: { color: '#f39c12' },
  statDanger: { color: '#e74c3c' },
  statValue: {
    fontSize: '32px',
    fontWeight: '700',
    margin: 0
  },
  filters: {
    background: 'white',
    padding: '20px',
    borderRadius: '12px',
    marginBottom: '20px',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '15px'
  },
  input: {
    padding: '10px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '14px'
  },
  tableContainer: {
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    overflow: 'hidden'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse'
  },
  th: {
    padding: '16px',
    textAlign: 'left',
    background: '#f8f9fa',
    fontWeight: '600',
    fontSize: '14px',
    textTransform: 'uppercase'
  },
  td: {
    padding: '16px',
    borderTop: '1px solid #ecf0f1'
  },
  tr: {
    cursor: 'pointer',
    transition: 'background 0.2s'
  },
  statusBadge: {
    display: 'inline-block',
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600'
  },
  actions: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap'
  },
  btnSm: {
    padding: '6px 12px',
    border: 'none',
    borderRadius: '6px',
    fontSize: '12px',
    cursor: 'pointer',
    color: 'white',
    fontWeight: '600'
  },
  btnQrcode: { background: '#9b59b6' },
  btnCopy: { background: '#1abc9c' },
  btnSuccess: { background: '#27ae60' },
  btnDanger: { background: '#dc3545' },
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modalContent: {
    background: 'white',
    padding: '30px',
    borderRadius: '12px',
    maxWidth: '600px',
    width: '90%',
    maxHeight: '90vh',
    overflow: 'auto'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px'
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer'
  },
  pixCode: {
    background: '#f8f9fa',
    padding: '15px',
    borderRadius: '8px',
    marginTop: '20px'
  },
  loading: {
    textAlign: 'center',
    padding: '40px',
    color: '#7f8c8d'
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#7f8c8d'
  }
};